<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcInvoiceLineGroup',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    invoice: {
      type: Object,
      required: true,
    },
    group: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const editing = ref(false);
  const expenseTypes = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    editing: editing.value,
    expenseTypes: expenseTypes.value,
    ux: ux.value,
  }));

  const $$load_expenseTypes = () => {
    return $fn.value.fetch('list_object_by_type', {
      parameters: { object_type: 'expense-type' },
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'editing' }, { name: 'expenseTypes' }, { name: 'ux' }],
    loaders: { expenseTypes: $$load_expenseTypes },
    ux: {},
    dataRefs: { editing, expenseTypes, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.invoice,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('invoice');
    }
  );
  watch(
    () => props.group,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('group');
    }
  );

  const act_4280711203779102_emit_nmfxlbv2h_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_4280711203779102_close_6u7vvulnc_getWhen = async ($event) => {
    return !!$event.data.modalResult.value;
  };
  const act_4280711203779102_showModal_yo9qitfhu_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'InvoiceLineGroup',
      props: {
        invoice: props.invoice,
        groupId: props.group.data.id || null,
        isDefaultGroup: !props.group.data.id,
      },
    };
  };

  const act_4280711203779102_emit_nmfxlbv2h = new actionTypes.EmitAction({
    actionArgs: act_4280711203779102_emit_nmfxlbv2h_getActionArgs,
  });
  const inst_evh_4280711203779102_close_6u7vvulnc =
    new actionTypes.EventHandlerImpl({
      action: act_4280711203779102_emit_nmfxlbv2h,
      event: 'close',
      when: inst_evh_4280711203779102_close_6u7vvulnc_getWhen,
      displayName: 'emit',
    });
  const act_4280711203779102_showModal_yo9qitfhu =
    new actionTypes.ShowModalAction({
      actionArgs: act_4280711203779102_showModal_yo9qitfhu_getActionArgs,
      events: [inst_evh_4280711203779102_close_6u7vvulnc],
    });
  const inst_evh_4280711203779102_clickPreventStop_50ddvakn4 =
    new actionTypes.EventHandlerImpl({
      action: act_4280711203779102_showModal_yo9qitfhu,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_4280711203779102_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_4280711203779102_clickPreventStop_50ddvakn4],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_4280711203779102_close_6u7vvulnc,
    inst_evh_4280711203779102_clickPreventStop_50ddvakn4,
    inst_evh_4280711203779102_clickPreventStop,
  };

  const evh_4280711203779102_close_6u7vvulnc = async (event, scope) => {
    return await actions.inst_evh_4280711203779102_close_6u7vvulnc.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4280711203779102_clickPreventStop_50ddvakn4 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4280711203779102_clickPreventStop_50ddvakn4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4280711203779102_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_4280711203779102_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpBox direction="row" class="w-100">
    <ItpText strong>
      <ItpIcon icon="material-symbols:ad-group-sharp"> </ItpIcon>
      <ItpText spacing="mx-1" :text="group.data.description"> </ItpText>
    </ItpText>
    <ItpBadge
      strong
      no-box-shadow
      class="mx-2 bg-gray-100 border"
      v-if="group.data.expense_type"
    >
      <ItpText strong :text="group.data.expense_type"> </ItpText>
    </ItpBadge>
    <UcRentalMiniCard
      badge
      :item="group.data.rental"
      class="mx-2"
      v-if="group.data.rental"
    >
    </UcRentalMiniCard>
    <UcPropertyMiniCard
      badge
      :item="group.data.property"
      class="mx-2"
      v-if="group.data.property"
    >
    </UcPropertyMiniCard>
    <UcWorksheetMiniCard
      badge
      :item="group.data.worksheet"
      class="mx-2"
      v-if="group.data.worksheet"
    >
    </UcWorksheetMiniCard>
    <UcCompanyMiniCard
      badge
      :item="group.data.contractor"
      class="mx-2"
      v-if="group.data.contractor"
    >
    </UcCompanyMiniCard>
    <ItpLink
      :icon="$config.editIcon"
      :text="pgettext('InvoiceLineGroup', 'Edit group...')"
      class="mx-2"
      @click.prevent.stop="evh_4280711203779102_clickPreventStop($event, {})"
    >
    </ItpLink>
  </ItpBox>
</template>
